/**
 * Translation Service
 * Handles translation of text, files, and folders using LLM Proxy
 */
import { promises as fs } from 'fs';
import path from 'path';
import pLimit from 'p-limit';
import pRetry from 'p-retry';
import { LlmProxyService } from './llm-proxy.service.js';
import type { CompletionMessage } from '../types/llm.types.js';
import type {
    TranslateOptions,
    TranslationResult,
    FileTranslationResult,
    FolderTranslationResult,
    TranslationProgress,
    TRANSLATABLE_EXTENSIONS,
    TranslatableExtension,
} from '../types/translate.types.js';

const TRANSLATABLE_EXTS = ['.md', '.txt', '.json', '.yaml', '.yml'] as const;

export class TranslationService {
    private readonly llmService: LlmProxyService;
    private readonly options: TranslateOptions;
    private readonly model: string;

    constructor(llmService: LlmProxyService, options: TranslateOptions, model?: string) {
        this.llmService = llmService;
        this.options = options;
        this.model = model || 'gpt-5.1-codex-mini'; // Default fast model for translation
    }

    /**
     * Detect input type
     */
    async detectInputType(input: string): Promise<'text' | 'file' | 'folder'> {
        try {
            const stat = await fs.stat(input);
            if (stat.isDirectory()) return 'folder';
            if (stat.isFile()) return 'file';
        } catch {
            // Path doesn't exist, treat as text
        }
        return 'text';
    }

    /**
     * Translate text string
     */
    async translateText(text: string): Promise<TranslationResult> {
        try {
            const translatedContent = await this.translateWithRetry(text, 'text');
            return {
                success: true,
                inputType: 'text',
                input: text,
                output: translatedContent,
                targetLanguage: this.options.to,
                translatedContent,
            };
        } catch (error) {
            return {
                success: false,
                inputType: 'text',
                input: text,
                output: '',
                targetLanguage: this.options.to,
                error: error instanceof Error ? error.message : 'Unknown error',
            };
        }
    }

    /**
     * Translate single file
     */
    async translateFile(filePath: string): Promise<FileTranslationResult> {
        try {
            // Read file content
            const content = await fs.readFile(filePath, 'utf-8');

            // Detect file type
            const ext = path.extname(filePath).toLowerCase();
            const fileType = this.getFileType(ext);

            // Translate content
            const translatedContent = await this.translateWithRetry(content, fileType);

            // Generate output path
            const outputPath = this.generateOutputPath(filePath);

            // Write output (unless dry-run)
            if (!this.options.dryRun) {
                await fs.writeFile(outputPath, translatedContent, 'utf-8');
            }

            return {
                inputPath: filePath,
                outputPath,
                success: true,
            };
        } catch (error) {
            return {
                inputPath: filePath,
                outputPath: '',
                success: false,
                error: error instanceof Error ? error.message : 'Unknown error',
            };
        }
    }

    /**
     * Translate entire folder recursively
     */
    async translateFolder(
        folderPath: string,
        onProgress?: (progress: TranslationProgress) => void
    ): Promise<FolderTranslationResult> {
        const files = await this.discoverFiles(folderPath);
        const concurrency = parseInt(this.options.concurrency || '3', 10);
        const limit = pLimit(concurrency);

        const results: FileTranslationResult[] = [];
        let current = 0;

        const tasks = files.map((file) =>
            limit(async () => {
                current++;
                onProgress?.({
                    current,
                    total: files.length,
                    currentFile: file,
                    status: 'translating',
                });

                const result = await this.translateFile(file);
                results.push(result);

                onProgress?.({
                    current,
                    total: files.length,
                    currentFile: file,
                    status: result.success ? 'done' : 'error',
                });

                return result;
            })
        );

        await Promise.all(tasks);

        return this.summarizeResults(folderPath, results);
    }

    /**
     * Generate output path with language suffix
     */
    private generateOutputPath(inputPath: string): string {
        if (this.options.output) {
            return this.options.output;
        }

        const ext = path.extname(inputPath);
        const base = path.basename(inputPath, ext);
        const dir = path.dirname(inputPath);
        const lang = this.options.to;

        return path.join(dir, `${base}.${lang}${ext}`);
    }

    /**
     * Discover translatable files in folder recursively
     */
    private async discoverFiles(folderPath: string): Promise<string[]> {
        const entries = await fs.readdir(folderPath, { withFileTypes: true });
        const files: string[] = [];

        for (const entry of entries) {
            const fullPath = path.join(folderPath, entry.name);

            if (entry.isDirectory()) {
                // Skip hidden directories and node_modules
                if (!entry.name.startsWith('.') && entry.name !== 'node_modules') {
                    const subFiles = await this.discoverFiles(fullPath);
                    files.push(...subFiles);
                }
            } else if (entry.isFile()) {
                const ext = path.extname(entry.name).toLowerCase();
                if (TRANSLATABLE_EXTS.includes(ext as TranslatableExtension)) {
                    // Skip already translated files (e.g., file.vi.md)
                    if (!this.isTranslatedFile(entry.name)) {
                        files.push(fullPath);
                    }
                }
            }
        }

        return files;
    }

    /**
     * Check if filename indicates already translated file
     */
    private isTranslatedFile(filename: string): boolean {
        // Pattern: .{lang}.{ext} (e.g., .vi.md, .en.txt)
        const pattern = /\.[a-z]{2}\.(md|txt|json|ya?ml)$/i;
        return pattern.test(filename);
    }

    /**
     * Build translation prompt based on content type
     */
    private buildPrompt(content: string, fileType: string): CompletionMessage[] {
        const targetLang = this.options.to;
        let systemPrompt = '';

        switch (fileType) {
            case 'markdown':
                systemPrompt = `You are a professional translator.
Translate the following Markdown content to ${targetLang}.
Preserve all formatting including:
- Markdown syntax (headers, lists, code blocks, links, tables)
- Line breaks and paragraph structure
- Code snippets (do not translate code, only comments if present)
- HTML tags if present

Return ONLY the translated content, no explanations.`;
                break;

            case 'json':
                systemPrompt = `You are translating a JSON localization file.
Translate all string values to ${targetLang}.
Preserve the JSON structure and keys exactly.
Return valid JSON only, no explanations.`;
                break;

            case 'yaml':
                systemPrompt = `You are translating a YAML configuration file.
Translate all string values to ${targetLang}.
Preserve the YAML structure and keys exactly.
Return valid YAML only, no explanations.`;
                break;

            default:
                systemPrompt = `You are a professional translator.
Translate the following text to ${targetLang}.
Preserve all formatting and line breaks.
Return ONLY the translated content, no explanations.`;
        }

        return [
            { role: 'system', content: systemPrompt },
            { role: 'user', content: content },
        ];
    }

    /**
     * Get file type from extension
     */
    private getFileType(ext: string): string {
        switch (ext.toLowerCase()) {
            case '.md':
                return 'markdown';
            case '.json':
                return 'json';
            case '.yaml':
            case '.yml':
                return 'yaml';
            default:
                return 'text';
        }
    }

    /**
     * Translate with retry logic
     */
    private async translateWithRetry(content: string, fileType: string): Promise<string> {
        return pRetry(
            async () => {
                const messages = this.buildPrompt(content, fileType);
                const result = await this.llmService.chat(messages, this.model);
                return result;
            },
            {
                retries: 3,
                onFailedAttempt: (error) => {
                    if (error.message.includes('rate limit')) {
                        console.log('⚠️  Rate limit, đang thử lại...');
                    }
                },
            }
        );
    }

    /**
     * Summarize folder translation results
     */
    private summarizeResults(
        folderPath: string,
        results: FileTranslationResult[]
    ): FolderTranslationResult {
        const summary = {
            total: results.length,
            success: results.filter((r) => r.success).length,
            failed: results.filter((r) => !r.success).length,
            skipped: 0,
        };

        return {
            inputFolder: folderPath,
            outputFolder: this.options.output || folderPath,
            results,
            summary,
        };
    }
}
